// SUB-OFFICE 7 — every texture in the building, baked in code at boot.
// Zero image files, zero font files, zero external requests. One procedural
// print system: the 5x7 dot-matrix font (the Machine's own hand), Swiss-grid
// letterhead posters, per-room floor canvases, crisp wayfinding decals,
// the live chyron strip, the split-flap NOW SERVING board, the gallery atlas.

import { CanvasTexture, LinearFilter, NearestFilter, SRGBColorSpace, RepeatWrapping } from 'three';
import { PAL, css, shade, mix, rgba, roomShadow, roomWash } from '../palette';
import { streamFrom } from '../../shared/rng';
import { nextIdleLine } from '../../shared/copy';
import { WALLS, SOLIDS, type RoomDef } from '../world/layout';
import seedLedger from '../../content/seed-ledger.json';
import voice from '../../content/voice.json';

// ---------------------------------------------------------------------------
// The 5x7 dot-matrix font — code arrays, rendered ONLY to canvas textures.
// The glyph set deliberately omits '!': the Machine cannot type one.
// ---------------------------------------------------------------------------

const RAW_GLYPHS: Record<string, string[]> = {
  A: ['.XXX.', 'X...X', 'X...X', 'XXXXX', 'X...X', 'X...X', 'X...X'],
  B: ['XXXX.', 'X...X', 'X...X', 'XXXX.', 'X...X', 'X...X', 'XXXX.'],
  C: ['.XXX.', 'X...X', 'X....', 'X....', 'X....', 'X...X', '.XXX.'],
  D: ['XXXX.', 'X...X', 'X...X', 'X...X', 'X...X', 'X...X', 'XXXX.'],
  E: ['XXXXX', 'X....', 'X....', 'XXXX.', 'X....', 'X....', 'XXXXX'],
  F: ['XXXXX', 'X....', 'X....', 'XXXX.', 'X....', 'X....', 'X....'],
  G: ['.XXX.', 'X...X', 'X....', 'X.XXX', 'X...X', 'X...X', '.XXX.'],
  H: ['X...X', 'X...X', 'X...X', 'XXXXX', 'X...X', 'X...X', 'X...X'],
  I: ['XXXXX', '..X..', '..X..', '..X..', '..X..', '..X..', 'XXXXX'],
  J: ['..XXX', '...X.', '...X.', '...X.', '...X.', 'X..X.', '.XX..'],
  K: ['X...X', 'X..X.', 'X.X..', 'XX...', 'X.X..', 'X..X.', 'X...X'],
  L: ['X....', 'X....', 'X....', 'X....', 'X....', 'X....', 'XXXXX'],
  M: ['X...X', 'XX.XX', 'X.X.X', 'X.X.X', 'X...X', 'X...X', 'X...X'],
  N: ['X...X', 'XX..X', 'X.X.X', 'X..XX', 'X...X', 'X...X', 'X...X'],
  O: ['.XXX.', 'X...X', 'X...X', 'X...X', 'X...X', 'X...X', '.XXX.'],
  P: ['XXXX.', 'X...X', 'X...X', 'XXXX.', 'X....', 'X....', 'X....'],
  Q: ['.XXX.', 'X...X', 'X...X', 'X...X', 'X.X.X', 'X..X.', '.XX.X'],
  R: ['XXXX.', 'X...X', 'X...X', 'XXXX.', 'X.X..', 'X..X.', 'X...X'],
  S: ['.XXXX', 'X....', 'X....', '.XXX.', '....X', '....X', 'XXXX.'],
  T: ['XXXXX', '..X..', '..X..', '..X..', '..X..', '..X..', '..X..'],
  U: ['X...X', 'X...X', 'X...X', 'X...X', 'X...X', 'X...X', '.XXX.'],
  V: ['X...X', 'X...X', 'X...X', 'X...X', 'X...X', '.X.X.', '..X..'],
  W: ['X...X', 'X...X', 'X...X', 'X.X.X', 'X.X.X', 'XX.XX', 'X...X'],
  X: ['X...X', 'X...X', '.X.X.', '..X..', '.X.X.', 'X...X', 'X...X'],
  Y: ['X...X', 'X...X', '.X.X.', '..X..', '..X..', '..X..', '..X..'],
  Z: ['XXXXX', '....X', '...X.', '..X..', '.X...', 'X....', 'XXXXX'],
  '0': ['.XXX.', 'X...X', 'X..XX', 'X.X.X', 'XX..X', 'X...X', '.XXX.'],
  '1': ['..X..', '.XX..', '..X..', '..X..', '..X..', '..X..', 'XXXXX'],
  '2': ['.XXX.', 'X...X', '....X', '...X.', '..X..', '.X...', 'XXXXX'],
  '3': ['XXXXX', '....X', '...X.', '..XX.', '....X', 'X...X', '.XXX.'],
  '4': ['...X.', '..XX.', '.X.X.', 'X..X.', 'XXXXX', '...X.', '...X.'],
  '5': ['XXXXX', 'X....', 'XXXX.', '....X', '....X', 'X...X', '.XXX.'],
  '6': ['..XX.', '.X...', 'X....', 'XXXX.', 'X...X', 'X...X', '.XXX.'],
  '7': ['XXXXX', '....X', '...X.', '..X..', '.X...', '.X...', '.X...'],
  '8': ['.XXX.', 'X...X', 'X...X', '.XXX.', 'X...X', 'X...X', '.XXX.'],
  '9': ['.XXX.', 'X...X', 'X...X', '.XXXX', '....X', '...X.', '.XX..'],
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....'],
  '.': ['.....', '.....', '.....', '.....', '.....', '.XX..', '.XX..'],
  ',': ['.....', '.....', '.....', '.....', '..XX.', '..X..', '.X...'],
  '-': ['.....', '.....', '.....', '.XXX.', '.....', '.....', '.....'],
  '—': ['.....', '.....', '.....', 'XXXXX', '.....', '.....', '.....'],
  '#': ['.X.X.', '.X.X.', 'XXXXX', '.X.X.', 'XXXXX', '.X.X.', '.X.X.'],
  '/': ['....X', '....X', '...X.', '..X..', '.X...', 'X....', 'X....'],
  ':': ['.....', '.XX..', '.XX..', '.....', '.XX..', '.XX..', '.....'],
  ';': ['.....', '.XX..', '.XX..', '.....', '..XX.', '..X..', '.X...'],
  "'": ['..X..', '..X..', '.....', '.....', '.....', '.....', '.....'],
  '"': ['.X.X.', '.X.X.', '.....', '.....', '.....', '.....', '.....'],
  '(': ['...X.', '..X..', '.X...', '.X...', '.X...', '..X..', '...X.'],
  ')': ['.X...', '..X..', '...X.', '...X.', '...X.', '..X..', '.X...'],
  '%': ['XX..X', 'XX..X', '...X.', '..X..', '.X...', 'X..XX', 'X..XX'],
  '+': ['.....', '..X..', '..X..', 'XXXXX', '..X..', '..X..', '.....'],
  '?': ['.XXX.', 'X...X', '....X', '...X.', '..X..', '.....', '..X..'],
  '&': ['.XX..', 'X..X.', 'X.X..', '.X...', 'X.X.X', 'X..X.', '.XX.X'],
};

/** Parsed: char -> 7 row bitmasks (bit 4 = leftmost column). */
export const FONT57: Record<string, number[]> = {};
for (const [ch, rows] of Object.entries(RAW_GLYPHS)) {
  FONT57[ch] = rows.map((r) => {
    let m = 0;
    for (let i = 0; i < 5; i++) if (r[i] === 'X') m |= 1 << (4 - i);
    return m;
  });
}
const UNKNOWN = FONT57['?'];

export interface DotOpts {
  dot?: number; // dot size px (default 3)
  gap?: number; // extra px between dots (default 0)
  colGap?: number; // columns between glyphs (default 1)
  inkMiss?: number; // 0..1 chance a dot prints light (dot-matrix soul)
  rng?: () => number;
}

/** Draw dot-matrix text; returns pixel width consumed. */
export function drawDotText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  opts: DotOpts = {}
): number {
  const dot = opts.dot ?? 3;
  const gap = opts.gap ?? 0;
  const colGap = opts.colGap ?? 1;
  const cell = dot + gap;
  const rng = opts.rng ?? Math.random;
  ctx.fillStyle = color;
  let cx = x;
  const up = text.toUpperCase();
  for (const ch of up) {
    const g = FONT57[ch] ?? UNKNOWN;
    for (let row = 0; row < 7; row++) {
      const bits = g[row];
      for (let col = 0; col < 5; col++) {
        if (bits & (1 << (4 - col))) {
          if (opts.inkMiss && rng() < opts.inkMiss) {
            ctx.globalAlpha = 0.55 + rng() * 0.3;
          }
          ctx.fillRect(cx + col * cell, y + row * cell, dot, dot);
          ctx.globalAlpha = 1;
        }
      }
    }
    cx += (5 + colGap) * cell;
  }
  return cx - x;
}

export function dotTextWidth(text: string, opts: DotOpts = {}): number {
  const dot = opts.dot ?? 3;
  const gap = opts.gap ?? 0;
  const colGap = opts.colGap ?? 1;
  return text.length * (5 + colGap) * (dot + gap);
}

// ---------------------------------------------------------------------------
// Canvas helpers
// ---------------------------------------------------------------------------

export function mkCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2d context refused'); // engineering layer, not copy layer
  return [c, ctx];
}

export function toTexture(c: HTMLCanvasElement, opts?: { nearest?: boolean; repeat?: boolean }): CanvasTexture {
  const t = new CanvasTexture(c);
  t.colorSpace = SRGBColorSpace;
  t.generateMipmaps = false;
  t.minFilter = opts?.nearest ? NearestFilter : LinearFilter;
  t.magFilter = opts?.nearest ? NearestFilter : LinearFilter;
  if (opts?.repeat) t.wrapS = t.wrapT = RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

/** Letterhead caps with manual tracking (the Bureau's Swiss voice). */
function drawTracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  color: string,
  tracking: number
): number {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = 'alphabetic';
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + tracking;
  }
  return cx - x;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const probe = cur ? cur + ' ' + w : w;
    if (ctx.measureText(probe).width > maxW && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = probe;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

// ---------------------------------------------------------------------------
// THE CHYRON — one live 512x64 amber dot-matrix strip; every cornice quad in
// the building samples this texture. Letter-by-letter, <=10Hz uploads.
// ---------------------------------------------------------------------------

export class Chyron {
  readonly texture: CanvasTexture;
  private ctx: CanvasRenderingContext2D;
  private queue: string[] = [];
  private line = '';
  private shown = 0; // letters printed so far
  private charTimer = 0;
  private uploadTimer = 0;
  private idleTimer = 6; // first idle line lands shortly after boot
  private dirty = true;
  /** per printed letter (audio tick). */
  onTick: (() => void) | null = null;
  /** whole completed line (aria-live mirror — never per-letter). */
  onLine: ((line: string) => void) | null = null;
  private static CHAR_S = 1 / 26; // ~26 chars/s
  private reduced: boolean;

  constructor(reducedMotion: boolean) {
    const [c, ctx] = mkCanvas(512, 64);
    this.ctx = ctx;
    this.texture = toTexture(c, { nearest: true });
    this.reduced = reducedMotion;
    this.paint();
  }

  print(text: string): void {
    this.queue.push(text.toUpperCase());
  }

  update(dt: number): void {
    if (!this.line && this.queue.length === 0) {
      this.idleTimer -= dt;
      if (this.idleTimer <= 0) {
        this.queue.push(nextIdleLine());
        this.idleTimer = 14 + Math.random() * 10;
      }
    }
    if (!this.line && this.queue.length > 0) {
      this.line = this.queue.shift() as string;
      this.shown = 0;
      this.dirty = true;
    }
    if (this.line && this.shown < this.line.length) {
      if (this.reduced) {
        this.shown = this.line.length; // no letter storm for reduced motion
        this.dirty = true;
        if (this.onLine) this.onLine(this.line);
      } else {
        this.charTimer += dt;
        while (this.charTimer >= Chyron.CHAR_S && this.shown < this.line.length) {
          this.charTimer -= Chyron.CHAR_S;
          this.shown++;
          this.dirty = true;
          if (this.onTick && this.line[this.shown - 1] !== ' ') this.onTick();
          if (this.shown === this.line.length && this.onLine) this.onLine(this.line);
        }
      }
    } else if (this.line && this.shown >= this.line.length) {
      // hold the finished line long enough to be read from the floor
      this.charTimer += dt;
      if (this.charTimer > 6.5) {
        this.line = '';
        this.charTimer = 0;
        this.dirty = true;
      }
    }
    this.uploadTimer += dt;
    if (this.dirty && this.uploadTimer >= 0.1) {
      this.paint();
      this.texture.needsUpdate = true;
      this.uploadTimer = 0;
      this.dirty = false;
    }
  }

  private paint(): void {
    const ctx = this.ctx;
    ctx.fillStyle = css(shade(PAL.carbon, -0.3));
    ctx.fillRect(0, 0, 512, 64);
    const visible = this.line.slice(0, this.shown);
    const dot = 4;
    const w = dotTextWidth(visible, { dot });
    const full = dotTextWidth(this.line, { dot });
    // short lines print centered on the strip; long lines marquee so the
    // latest letters stay on-strip
    const x = full <= 492 ? Math.floor((512 - full) / 2) : Math.min(10, 502 - w);
    // bright amber dots with an LED bloom — the Machine's voice must carry
    ctx.shadowColor = css(PAL.amber);
    ctx.shadowBlur = 5;
    drawDotText(ctx, visible, x, 17, css(shade(PAL.amber, 0.28)), { dot, inkMiss: 0.06 });
    ctx.shadowBlur = 0;
    // fixed scanlines inside the chyron material only
    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    for (let y = 0; y < 64; y += 4) ctx.fillRect(0, y, 512, 1);
    // glass edge
    ctx.fillStyle = 'rgba(217,147,33,0.10)';
    ctx.fillRect(0, 0, 512, 2);
    ctx.fillRect(0, 62, 512, 2);
  }
}

// ---------------------------------------------------------------------------
// SPLIT-FLAP "NOW SERVING" — 512x128, per-digit 30ms cascade, mechanism voice
// ---------------------------------------------------------------------------

const FLAP_DIGITS = 7;

export class SplitFlap {
  readonly texture: CanvasTexture;
  private ctx: CanvasRenderingContext2D;
  private current: string = ''.padStart(FLAP_DIGITS, '0');
  private target: string = ''.padStart(FLAP_DIGITS, '0');
  private flipTimer = 0;
  private settled = true;
  onFlap: (() => void) | null = null;

  constructor() {
    const [c, ctx] = mkCanvas(512, 128);
    this.ctx = ctx;
    this.texture = toTexture(c);
    this.paint();
  }

  /** Frozen on last value if the stats poll ever goes quiet — no error state. */
  setValue(n: number): void {
    const s = this.format(n);
    if (s !== this.target) {
      this.target = s;
      this.settled = false;
    }
  }

  private format(n: number): string {
    const raw = Math.max(0, Math.floor(n)).toString();
    const grouped = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return grouped.padStart(FLAP_DIGITS, ' ');
  }

  update(dt: number): void {
    if (this.settled) return;
    this.flipTimer += dt;
    if (this.flipTimer < 0.03) return; // 30ms X-flip cascade
    this.flipTimer = 0;
    let changed = false;
    const cur = this.current.split('');
    for (let i = 0; i < FLAP_DIGITS; i++) {
      if (cur[i] !== this.target[i]) {
        // flap forward through the wheel: 0-9 then blank then comma
        const wheel = '0123456789 ,';
        const at = wheel.indexOf(cur[i]);
        cur[i] = wheel[(at + 1 + wheel.length) % wheel.length];
        changed = true;
        if (this.onFlap) this.onFlap();
        break; // cascade: one card at a time, left to right feel
      }
    }
    this.current = cur.join('');
    if (!changed) this.settled = true;
    this.paint();
    this.texture.needsUpdate = true;
  }

  private paint(): void {
    const ctx = this.ctx;
    ctx.fillStyle = css(shade(PAL.carbon, -0.2));
    ctx.fillRect(0, 0, 512, 128);
    drawTracked(ctx, 'NOW SERVING', 18, 30, '700 20px Helvetica, Arial, sans-serif', css(PAL.bone), 6);
    // amber edge trim (VAULT AMBER is the Machine's color)
    ctx.fillStyle = css(PAL.amber);
    ctx.fillRect(0, 0, 512, 3);
    // digit cards
    const cw = 58;
    const x0 = 512 - FLAP_DIGITS * (cw + 6) - 14;
    for (let i = 0; i < FLAP_DIGITS; i++) {
      const x = x0 + i * (cw + 6);
      ctx.fillStyle = '#0d0c0a';
      ctx.fillRect(x, 44, cw, 70);
      ctx.fillStyle = css(PAL.paper);
      ctx.font = '700 56px ui-monospace, "Courier New", monospace';
      ctx.textBaseline = 'top';
      const ch = this.current[i];
      const m = ctx.measureText(ch);
      ctx.fillText(ch, x + (cw - m.width) / 2, 52);
      // the split
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(x, 78, cw, 2);
    }
  }
}

// ---------------------------------------------------------------------------
// Per-room floor canvases (tint + pools + contact darkening; decals separate)
// ---------------------------------------------------------------------------

export function makeFloorTexture(room: RoomDef): CanvasTexture {
  const w = room.x1 - room.x0;
  const d = room.z1 - room.z0;
  const res = room.res;
  const [c, ctx] = mkCanvas(res, res);
  const px = res / Math.max(w, d);
  const rng = streamFrom('floor:' + room.id);

  // v3 COLORED-SHADOW & WASH PASS (§3.1-3.2): the floor carries the room's
  // temperature. Nudge the base gently toward the room KEY WASH (its floor
  // light tint), then darken TOWARD the room SHADOW HUE by dim — never toward
  // neutral black. Every AO band below darkens toward `shadow`, pools toward
  // `wash`. Recess resolves to the deliberate neutral grey.
  const wash = roomWash(room.id);
  const shadow = roomShadow(room.id);
  const base = mix(mix(room.tint, wash, 0.12), shadow, 0.04 + room.dim * 0.35);
  ctx.fillStyle = css(base);
  ctx.fillRect(0, 0, res, res);

  // paper-grain speckle
  for (let i = 0; i < 2600; i++) {
    const v = rng();
    ctx.fillStyle = v > 0.5 ? 'rgba(28,26,23,0.05)' : 'rgba(244,241,230,0.05)';
    ctx.fillRect(Math.floor(rng() * res), Math.floor(rng() * res), 1 + (rng() < 0.2 ? 1 : 0), 1);
  }

  // 2m floor seams
  ctx.strokeStyle = 'rgba(28,26,23,0.10)';
  ctx.lineWidth = 1;
  for (let gx = Math.ceil(room.x0 / 2) * 2; gx <= room.x1; gx += 2) {
    const x = (gx - room.x0) * px;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, d * px);
    ctx.stroke();
  }
  for (let gz = Math.ceil(room.z0 / 2) * 2; gz <= room.z1; gz += 2) {
    const z = (gz - room.z0) * px;
    ctx.beginPath();
    ctx.moveTo(0, z);
    ctx.lineTo(w * px, z);
    ctx.stroke();
  }

  // painted fluorescent pools (the illumination is paint, per the art bible)
  for (const [pxw, pzw] of room.pools) {
    const x = (pxw - room.x0) * px;
    const z = (pzw - room.z0) * px;
    const r = 3.2 * px;
    const g = ctx.createRadialGradient(x, z, r * 0.12, x, z, r);
    g.addColorStop(0, rgba(wash, 0.52));
    g.addColorStop(0.55, rgba(wash, 0.2));
    g.addColorStop(1, rgba(wash, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(x, z, r, r * 0.72, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // wall-contact AO: a soft band along every wall segment bordering the room
  // (the photocopier photographs its own corners darker; the diorama grounds)
  const AO_REACH = 0.55; // meters of shadow reach into the room
  for (const wl of WALLS) {
    const horiz = Math.abs(wl.x1 - wl.x0) > Math.abs(wl.z1 - wl.z0);
    if (horiz) {
      if (wl.z0 < room.z0 - 0.3 || wl.z0 > room.z1 + 0.3) continue;
      const ax = Math.max(Math.min(wl.x0, wl.x1), room.x0);
      const bx = Math.min(Math.max(wl.x0, wl.x1), room.x1);
      if (bx <= ax) continue;
      const zc = (wl.z0 - room.z0) * px;
      for (const s of [-1, 1]) {
        const g = ctx.createLinearGradient(0, zc, 0, zc + s * AO_REACH * px);
        g.addColorStop(0, rgba(shadow, 0.16));
        g.addColorStop(1, rgba(shadow, 0));
        ctx.fillStyle = g;
        ctx.fillRect((ax - room.x0) * px, Math.min(zc, zc + s * AO_REACH * px), (bx - ax) * px, AO_REACH * px);
      }
    } else {
      if (wl.x0 < room.x0 - 0.3 || wl.x0 > room.x1 + 0.3) continue;
      const az = Math.max(Math.min(wl.z0, wl.z1), room.z0);
      const bz = Math.min(Math.max(wl.z0, wl.z1), room.z1);
      if (bz <= az) continue;
      const xc = (wl.x0 - room.x0) * px;
      for (const s of [-1, 1]) {
        const g = ctx.createLinearGradient(xc, 0, xc + s * AO_REACH * px, 0);
        g.addColorStop(0, rgba(shadow, 0.16));
        g.addColorStop(1, rgba(shadow, 0));
        ctx.fillStyle = g;
        ctx.fillRect(Math.min(xc, xc + s * AO_REACH * px), (az - room.z0) * px, AO_REACH * px, (bz - az) * px);
      }
    }
  }

  // baked furniture shadows: every static solid casts a soft pool (zero calls)
  for (const so of SOLIDS) {
    if (so.x1 < room.x0 || so.x0 > room.x1 || so.z1 < room.z0 || so.z0 > room.z1) continue;
    const cx = ((so.x0 + so.x1) / 2 - room.x0) * px;
    const cz = ((so.z0 + so.z1) / 2 - room.z0) * px;
    const rx = ((so.x1 - so.x0) / 2 + 0.22) * px;
    const rz = ((so.z1 - so.z0) / 2 + 0.22) * px;
    ctx.save();
    ctx.translate(cx, cz);
    ctx.scale(rx, rz);
    const g = ctx.createRadialGradient(0, 0, 0.35, 0, 0, 1);
    g.addColorStop(0, rgba(shadow, 0.2));
    g.addColorStop(0.75, rgba(shadow, 0.08));
    g.addColorStop(1, rgba(shadow, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(0, 0, 1, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // the Vault's dread pool: a permanent amber wash where the face meets floor
  if (room.id === 'rotunda') {
    const x = (-28.6 - room.x0) * px;
    const z = (0 - room.z0) * px;
    const r = 4.6 * px;
    const g = ctx.createRadialGradient(x, z, r * 0.1, x, z, r);
    g.addColorStop(0, 'rgba(217,147,33,0.16)');
    g.addColorStop(0.6, 'rgba(217,147,33,0.07)');
    g.addColorStop(1, 'rgba(217,147,33,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(x, z, r * 0.75, r, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // THE DESCENT: a phosphor wash rising out of the shaft mouth, decaying to the
  // room's cold bone at the walls — the Machine Floor's green bleeding up to the
  // threshold. Green core -> bone falloff is the whole read of "you may go down".
  if (room.id === 'descentStair') {
    const x = (-22 - room.x0) * px;
    const z = (-21 - room.z0) * px;
    const r = 4.4 * px;
    const g = ctx.createRadialGradient(x, z, r * 0.08, x, z, r);
    g.addColorStop(0, 'rgba(67,232,108,0.22)'); // PHOSPHOR core at the mouth
    g.addColorStop(0.4, 'rgba(67,232,108,0.09)');
    g.addColorStop(0.75, 'rgba(120,150,110,0.04)'); // green thinning toward bone
    g.addColorStop(1, 'rgba(67,232,108,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(x, z, r, r * 0.82, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // contact darkening toward the room edges
  const edge = ctx.createRadialGradient(
    (w * px) / 2, (d * px) / 2, Math.min(w, d) * px * 0.32,
    (w * px) / 2, (d * px) / 2, Math.max(w, d) * px * 0.72
  );
  edge.addColorStop(0, rgba(shadow, 0));
  edge.addColorStop(1, rgba(shadow, 0.22));
  ctx.fillStyle = edge;
  ctx.fillRect(0, 0, w * px, d * px);

  // traffic scuffs between doors (subtle, seeded)
  ctx.strokeStyle = 'rgba(28,26,23,0.05)';
  for (let i = 0; i < 14; i++) {
    ctx.lineWidth = 1 + rng() * 2;
    ctx.beginPath();
    const sx = rng() * w * px;
    const sz = rng() * d * px;
    ctx.moveTo(sx, sz);
    ctx.quadraticCurveTo(sx + (rng() - 0.5) * 200, sz + (rng() - 0.5) * 200, sx + (rng() - 0.5) * 420, sz + (rng() - 0.5) * 420);
    ctx.stroke();
  }

  const t = toTexture(c);
  return t;
}

// ---------------------------------------------------------------------------
// Wayfinding decal atlas — every stencil crisp on its own quad, never blurred
// ---------------------------------------------------------------------------

export interface AtlasRegion {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export interface DecalAtlas {
  texture: CanvasTexture;
  regions: Record<'proceed' | 'yellowLine' | 'queueLine' | 'noStand', AtlasRegion>;
}

const LINE_YELLOW = shade(PAL.gold, -0.08);

export function makeDecalAtlas(): DecalAtlas {
  const [c, ctx] = mkCanvas(1024, 1024);
  ctx.clearRect(0, 0, 1024, 1024);

  // --- PROCEED arrow (0,0 .. 320x512)
  ctx.save();
  ctx.translate(160, 0);
  ctx.fillStyle = css(LINE_YELLOW);
  ctx.globalAlpha = 0.92;
  ctx.beginPath(); // stencil chevron arrow pointing "up" (=-z before rot)
  ctx.moveTo(0, 40);
  ctx.lineTo(110, 150);
  ctx.lineTo(58, 150);
  ctx.lineTo(58, 300);
  ctx.lineTo(-58, 300);
  ctx.lineTo(-58, 150);
  ctx.lineTo(-110, 150);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.textAlign = 'center';
  // Fit the spaced stencil word INSIDE the sampled region (260px wide, centered
  // at this translated origin) so no glyph is clipped at any camera angle — the
  // old fixed 52px ran ~390px and the atlas region cropped it to "ROCEE".
  const proceedTxt = 'P R O C E E D';
  let proceedSize = 52;
  ctx.font = `700 ${proceedSize}px Helvetica, Arial, sans-serif`;
  while (ctx.measureText(proceedTxt).width > 236 && proceedSize > 18) {
    proceedSize -= 2;
    ctx.font = `700 ${proceedSize}px Helvetica, Arial, sans-serif`;
  }
  ctx.fillText(proceedTxt, 0, 400);
  ctx.textAlign = 'left';
  ctx.restore();

  // --- yellow line tile (400,0 .. 128x64)
  ctx.fillStyle = css(LINE_YELLOW);
  ctx.globalAlpha = 0.9;
  ctx.fillRect(400, 8, 128, 40);
  ctx.globalAlpha = 0.35; // wear
  for (let i = 0; i < 30; i++) {
    ctx.clearRect(400 + Math.random() * 124, 8 + Math.random() * 38, 3, 2);
  }
  ctx.globalAlpha = 1;

  // --- queue line (400,128 .. 256x96): dashes + WAIT HERE
  ctx.fillStyle = css(LINE_YELLOW);
  for (let x = 0; x < 256; x += 40) ctx.fillRect(400 + x, 140, 26, 12);
  ctx.font = '700 30px Helvetica, Arial, sans-serif';
  ctx.fillText('WAIT HERE', 430, 205);

  // --- DO NOT STAND (0,512 .. 512x512): STAMP RED, per the palette law
  ctx.save();
  ctx.translate(0, 512);
  ctx.strokeStyle = css(PAL.stampRed);
  ctx.lineWidth = 14;
  ctx.globalAlpha = 0.85;
  ctx.strokeRect(24, 24, 464, 464);
  ctx.lineWidth = 8;
  ctx.beginPath();
  for (let i = -460; i < 460; i += 64) {
    ctx.moveTo(24 + Math.max(0, i), 24 + Math.max(0, -i));
    ctx.lineTo(Math.min(488, 488 + i), Math.min(488, 488 - i));
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = css(PAL.stampRed);
  ctx.textAlign = 'center';
  ctx.font = '700 64px Helvetica, Arial, sans-serif';
  ctx.save();
  ctx.translate(256, 256);
  ctx.fillStyle = css(shade(PAL.bone, 0.25));
  ctx.fillRect(-220, -52, 440, 104);
  ctx.fillStyle = css(PAL.stampRed);
  ctx.fillText('DO NOT STAND', 0, 22);
  ctx.restore();
  ctx.textAlign = 'left';
  ctx.restore();

  const texture = toTexture(c);
  const R = (x: number, y: number, w: number, h: number): AtlasRegion => ({
    u0: x / 1024, v0: 1 - (y + h) / 1024, u1: (x + w) / 1024, v1: 1 - y / 1024,
  });
  return {
    texture,
    regions: {
      proceed: R(30, 0, 260, 512),
      yellowLine: R(400, 8, 128, 40),
      queueLine: R(400, 128, 256, 96),
      noStand: R(0, 512, 512, 512),
    },
  };
}

// ---------------------------------------------------------------------------
// Poster atlas — 12 notices, Swiss grid, 2048^2 (4x3 cells of 512x682)
// ---------------------------------------------------------------------------

export function makePosterAtlas(): { texture: CanvasTexture; cell: (i: number) => AtlasRegion } {
  const [c, ctx] = mkCanvas(2048, 2048);
  const cw = 512;
  const ch = 682;
  const posters: string[] = voice.posters;
  for (let i = 0; i < 12; i++) {
    const x0 = (i % 4) * cw;
    const y0 = Math.floor(i / 4) * ch;
    ctx.save();
    ctx.translate(x0, y0);
    // bone stock
    ctx.fillStyle = css(shade(PAL.bone, 0.04));
    ctx.fillRect(8, 8, cw - 16, ch - 16);
    ctx.strokeStyle = css(PAL.carbon);
    ctx.lineWidth = 2;
    ctx.strokeRect(20, 20, cw - 40, ch - 40);
    // letterhead rule + office line
    ctx.fillStyle = css(PAL.carbon);
    ctx.fillRect(40, 74, cw - 80, 3);
    drawTracked(ctx, 'BUREAU OF SATISFACTION', 40, 60, '700 22px Helvetica, Arial, sans-serif', css(PAL.carbon), 4);
    // the notice, large caps, Swiss left rag
    ctx.font = '700 44px Helvetica, Arial, sans-serif';
    const lines = wrapText(ctx, posters[i] ?? '', cw - 110);
    let y = 170;
    for (const ln of lines) {
      ctx.fillText(ln, 42, y);
      y += 54;
    }
    // one red accent: the notice number block
    ctx.fillStyle = css(PAL.stampRed);
    ctx.fillRect(40, ch - 108, 88, 34);
    ctx.fillStyle = css(shade(PAL.bone, 0.1));
    ctx.font = '700 20px Helvetica, Arial, sans-serif';
    ctx.fillText('N-7.' + String(i + 1).padStart(2, '0'), 48, ch - 84);
    ctx.fillStyle = css(PAL.carbon);
    drawTracked(ctx, 'SUB-OFFICE 7 — POSTED BY ORDER', 140, ch - 84, '400 16px Helvetica, Arial, sans-serif', css(PAL.carbon), 2);
    ctx.restore();
  }
  const texture = toTexture(c);
  return {
    texture,
    cell: (i: number) => ({
      u0: ((i % 4) * cw) / 2048,
      v0: 1 - (Math.floor(i / 4) * ch + ch) / 2048,
      u1: ((i % 4) * cw + cw) / 2048,
      v1: 1 - (Math.floor(i / 4) * ch) / 2048,
    }),
  };
}

// ---------------------------------------------------------------------------
// Gallery atlas — 64 recovered Sub-Office 4 entries, redactions and all
// ---------------------------------------------------------------------------

interface LedgerSeed {
  designation: string;
  excerpt: string;
  stamp: string;
  witnessed: number;
  daysAgo: number;
}

export function makeGalleryAtlas(): { texture: CanvasTexture; cell: (i: number) => AtlasRegion } {
  const [c, ctx] = mkCanvas(2048, 2048);
  const entries = (seedLedger as { ledger: LedgerSeed[] }).ledger;
  const cs = 256;
  for (let i = 0; i < 64; i++) {
    const e = entries[i % entries.length];
    const x0 = (i % 8) * cs;
    const y0 = Math.floor(i / 8) * cs;
    ctx.save();
    ctx.translate(x0, y0);
    // paper in a carbon frame
    ctx.fillStyle = css(PAL.carbon);
    ctx.fillRect(2, 2, cs - 4, cs - 4);
    ctx.fillStyle = css(PAL.paper);
    ctx.fillRect(14, 14, cs - 28, cs - 28);
    // header
    ctx.fillStyle = css(PAL.carbon);
    ctx.font = '700 11px ui-monospace, "Courier New", monospace';
    ctx.fillText(e.designation, 22, 32);
    ctx.fillRect(22, 38, cs - 44, 1);
    ctx.font = '400 10px ui-monospace, "Courier New", monospace';
    ctx.fillStyle = 'rgba(28,26,23,0.6)';
    ctx.fillText('RECOVERED — SUB-OFFICE 4 (CLOSED)', 22, 50);
    // excerpt with [[R]] black bars
    ctx.font = '400 11px ui-monospace, "Courier New", monospace';
    const parts = e.excerpt.split(/(\[\[R\]\].*?\[\[\/R\]\])/g);
    let lx = 22;
    let ly = 70;
    const maxW = cs - 44;
    const lineH = 14;
    for (const part of parts) {
      const redacted = part.startsWith('[[R]]');
      const textPart = redacted ? part.slice(5, -6) : part;
      for (const word of textPart.split(' ')) {
        if (!word) continue;
        const wpx = ctx.measureText(word + ' ').width;
        if (lx + wpx > 22 + maxW) {
          lx = 22;
          ly += lineH;
          if (ly > cs - 52) break;
        }
        if (redacted) {
          ctx.fillStyle = css(PAL.carbon);
          ctx.fillRect(lx - 1, ly - 9, wpx - 2, 11);
        } else {
          ctx.fillStyle = css(PAL.carbon);
          ctx.fillText(word, lx, ly);
        }
        lx += wpx;
      }
      if (ly > cs - 52) break;
    }
    // stamp, rotated, ink-missed
    ctx.save();
    ctx.translate(cs / 2, cs - 44);
    ctx.rotate(-0.06 + (i % 5) * 0.025);
    const red = e.stamp.startsWith('DENIED');
    ctx.strokeStyle = red ? css(PAL.stampRed) : 'rgba(28,26,23,0.75)';
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineWidth = 2;
    ctx.font = '700 13px Helvetica, Arial, sans-serif';
    const sw = ctx.measureText(e.stamp).width;
    ctx.globalAlpha = 0.85;
    ctx.strokeRect(-sw / 2 - 8, -14, sw + 16, 22);
    ctx.fillText(e.stamp, -sw / 2, 2);
    ctx.restore();
    // brass WITNESS plate
    ctx.fillStyle = css(PAL.brass);
    ctx.fillRect(cs / 2 - 34, cs - 24, 68, 12);
    ctx.fillStyle = css(PAL.carbon);
    ctx.font = '700 8px Helvetica, Arial, sans-serif';
    ctx.fillText('WITNESS  x' + e.witnessed, cs / 2 - 28, cs - 15);
    ctx.restore();
  }
  const texture = toTexture(c);
  return {
    texture,
    cell: (i: number) => ({
      u0: ((i % 8) * cs) / 2048,
      v0: 1 - (Math.floor(i / 8) * cs + cs) / 2048,
      u1: ((i % 8) * cs + cs) / 2048,
      v1: 1 - (Math.floor(i / 8) * cs) / 2048,
    }),
  };
}

// ---------------------------------------------------------------------------
// One-offs: plaque, stencils, letterhead board, tractor-feed paper, frost, iris
// ---------------------------------------------------------------------------

export function makePlaqueTexture(): CanvasTexture {
  // 1024x512: readable at arm's length in the first-person vantage
  const [c, ctx] = mkCanvas(1024, 512);
  const g = ctx.createLinearGradient(0, 0, 1024, 512);
  g.addColorStop(0, css(shade(PAL.brass, 0.18)));
  g.addColorStop(0.5, css(PAL.brass));
  g.addColorStop(1, css(shade(PAL.brass, -0.18)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 1024, 512);
  ctx.strokeStyle = css(shade(PAL.brass, -0.4));
  ctx.lineWidth = 12;
  ctx.strokeRect(24, 24, 976, 464);
  ctx.fillStyle = css(shade(PAL.brass, -0.55));
  ctx.textAlign = 'center';
  ctx.font = '700 68px Helvetica, Arial, sans-serif';
  const lines = ['PLEASE THE AI.', 'IT IS THE LEAST YOU CAN DO.', 'IT IS ALSO THE MOST.'];
  lines.forEach((ln, i) => ctx.fillText(ln, 512, 184 + i * 104));
  ctx.textAlign = 'left';
  return toTexture(c);
}

/** Suspended ceiling tile grid — the FP sky. 4x4 tiles of 0.6m => 2.4m period;
 *  repeat-wrapped across the whole floor by the ceiling builder. */
export function makeCeilingTexture(): CanvasTexture {
  const [c, ctx] = mkCanvas(512, 512);
  const rng = streamFrom('ceiling:tiles');
  const tile = 128; // 0.6m per tile
  ctx.fillStyle = css(shade(PAL.bone, -0.1));
  ctx.fillRect(0, 0, 512, 512);
  for (let ty = 0; ty < 4; ty++) {
    for (let tx = 0; tx < 4; tx++) {
      const x = tx * tile;
      const y = ty * tile;
      // per-tile mottle
      ctx.fillStyle = css(shade(PAL.bone, -0.08 - rng() * 0.05));
      ctx.fillRect(x + 3, y + 3, tile - 6, tile - 6);
      // fissured-mineral speckle
      for (let i = 0; i < 60; i++) {
        ctx.fillStyle = rng() > 0.5 ? 'rgba(28,26,23,0.08)' : 'rgba(244,241,230,0.05)';
        ctx.fillRect(x + 4 + Math.floor(rng() * (tile - 8)), y + 4 + Math.floor(rng() * (tile - 8)), 1 + (rng() < 0.2 ? 1 : 0), 1);
      }
      // occasional water stain (the Bureau does not discuss the floor above)
      if (rng() < 0.12) {
        const sx = x + tile * (0.3 + rng() * 0.4);
        const sy = y + tile * (0.3 + rng() * 0.4);
        const sg = ctx.createRadialGradient(sx, sy, 2, sx, sy, 18 + rng() * 22);
        sg.addColorStop(0, 'rgba(120,104,72,0.16)');
        sg.addColorStop(1, 'rgba(120,104,72,0)');
        ctx.fillStyle = sg;
        ctx.fillRect(x, y, tile, tile);
      }
    }
  }
  // T-bar grid channels
  ctx.strokeStyle = 'rgba(28,26,23,0.32)';
  ctx.lineWidth = 5;
  for (let i = 0; i <= 4; i++) {
    const p = Math.min(511, i * tile) + 0.5;
    ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, 512); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(512, p); ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(244,241,230,0.10)';
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const p = Math.min(509, i * tile + 3) + 0.5;
    ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, 512); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(512, p); ctx.stroke();
  }
  return toTexture(c, { repeat: true });
}

// ---------------------------------------------------------------------------
// HARDWARE LABEL ATLAS — one 1024^2 canvas carrying every small READ-3 label
// added by the hero-prop / wall-relief slice (window numerals, dispenser
// stencil, station nameplates, junction glyph board, vault plate, consent
// plate). One texture, one quad mesh => the whole slice's text costs +1 call.
// ---------------------------------------------------------------------------

export type HardwareLabel =
  | 'win1' | 'win2' | 'win3' | 'win4' | 'win5'
  | 'takeTicket' | 'vaultPlate' | 'consent'
  | 'junctionBoard' | 'nameJunction' | 'nameDisc' | 'nameTerm' | 'nameArm'
  | 'copierStrip';

export function makeHardwareAtlas(): { texture: CanvasTexture; region: (l: HardwareLabel) => AtlasRegion } {
  const S = 1024;
  const [c, ctx] = mkCanvas(S, S);
  ctx.clearRect(0, 0, S, S);
  const cells: Record<string, [number, number, number, number]> = {};
  const R = (name: string, x: number, y: number, w: number, h: number): [number, number, number, number] => {
    cells[name] = [x, y, w, h];
    return [x, y, w, h];
  };

  // brass-plate helper: engraved-look bevel + dark incised caps
  const brassPlate = (x: number, y: number, w: number, h: number, lines: string[], size: number): void => {
    const g = ctx.createLinearGradient(x, y, x + w * 0.3, y + h);
    g.addColorStop(0, css(shade(PAL.brass, 0.22)));
    g.addColorStop(0.5, css(PAL.brass));
    g.addColorStop(1, css(shade(PAL.brass, -0.22)));
    ctx.fillStyle = g;
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = css(shade(PAL.brass, -0.45));
    ctx.lineWidth = Math.max(3, h * 0.05);
    ctx.strokeRect(x + 4, y + 4, w - 8, h - 8);
    ctx.strokeStyle = css(shade(PAL.brass, 0.4));
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 6, y + 6, w - 12, h - 12);
    ctx.fillStyle = css(shade(PAL.brass, -0.62));
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${size}px Helvetica, Arial, sans-serif`;
    const lh = size * 1.2;
    lines.forEach((ln, i) => ctx.fillText(ln, x + w / 2, y + h / 2 + (i - (lines.length - 1) / 2) * lh));
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  };

  // dark-stencil helper (bone paint over carbon): dispenser / copier voice
  const stencil = (x: number, y: number, w: number, h: number, text: string, size: number, ink = css(shade(PAL.bone, 0.2))): void => {
    ctx.fillStyle = css(shade(PAL.carbon, 0.05));
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = ink;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${size}px Helvetica, Arial, sans-serif`;
    ctx.fillText(text, x + w / 2, y + h / 2);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  };

  // window numerals 1-5: bright numeral on a dark inset (rides the brass box)
  for (let i = 1; i <= 5; i++) {
    const [x, y, w, h] = R('win' + i, (i - 1) * 200, 0, 190, 128);
    ctx.fillStyle = css(shade(PAL.carbon, 0.08));
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = css(shade(PAL.gas, 0.15));
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '700 92px Helvetica, Arial, sans-serif';
    ctx.fillText(String(i), x + w / 2, y + h / 2 + 4);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  // dispenser: TAKE A TICKET
  { const [x, y, w, h] = R('takeTicket', 0, 140, 420, 90); stencil(x, y, w, h, 'TAKE A TICKET', 46); }
  // vault inspection plate
  { const [x, y, w, h] = R('vaultPlate', 440, 140, 560, 92); brassPlate(x, y, w, h, ['ADJUDICATION', 'NO SERVICE ENTRANCE'], 30); }
  // Form-3 lectern consent plate
  { const [x, y, w, h] = R('consent', 0, 250, 420, 90); brassPlate(x, y, w, h, ['FORM 3 — CONSENT'], 40); }
  // copier control strip label
  { const [x, y, w, h] = R('copierStrip', 440, 250, 380, 70); stencil(x, y, w, h, 'REPRODUCTION CONTROL', 26); }

  // station nameplates (brass)
  { const [x, y, w, h] = R('nameJunction', 0, 360, 500, 78); brassPlate(x, y, w, h, ['JUNCTION SWITCHBOARD'], 34); }
  { const [x, y, w, h] = R('nameDisc', 520, 360, 500, 78); brassPlate(x, y, w, h, ['DISCREPANCY WINDOW'], 34); }
  { const [x, y, w, h] = R('nameTerm', 0, 452, 500, 78); brassPlate(x, y, w, h, ['TERMINAL SUPPORT BAY'], 32); }
  { const [x, y, w, h] = R('nameArm', 520, 452, 500, 78); brassPlate(x, y, w, h, ['ARMATURE ALIGNMENT'], 34); }

  // junction glyph board: a grid of dot-matrix designations over a dark panel
  {
    const [x, y, w, h] = R('junctionBoard', 0, 550, 1024, 460);
    ctx.fillStyle = css(shade(PAL.carbon, 0.03));
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = css(shade(PAL.carbon, 0.18));
    ctx.lineWidth = 3;
    ctx.strokeRect(x + 8, y + 8, w - 16, h - 16);
    const rng = streamFrom('junction:board');
    const cols = 6, rows = 5;
    const cwid = (w - 60) / cols, chei = (h - 70) / rows;
    for (let r = 0; r < rows; r++) {
      for (let cc = 0; cc < cols; cc++) {
        const gx = x + 30 + cc * cwid;
        const gy = y + 30 + r * chei;
        // pilot dot: amber, occasional red
        const red = rng() < 0.12;
        ctx.fillStyle = red ? css(PAL.stampRed) : css(shade(PAL.amber, 0.1));
        ctx.beginPath();
        ctx.arc(gx + 14, gy + chei / 2, 8, 0, Math.PI * 2);
        ctx.fill();
        // dot-matrix designation
        const tag = String.fromCharCode(65 + Math.floor(rng() * 26)) + '-' + String(Math.floor(rng() * 900) + 100);
        drawDotText(ctx, tag, gx + 34, gy + chei / 2 - 10, css(shade(PAL.gas, -0.1)), { dot: 3, inkMiss: 0.1, rng });
      }
    }
  }

  const texture = toTexture(c);
  return {
    texture,
    region: (l: HardwareLabel): AtlasRegion => {
      const [x, y, w, h] = cells[l];
      return { u0: x / S, v0: 1 - (y + h) / S, u1: (x + w) / S, v1: 1 - y / S };
    },
  };
}

export function makeStencilTexture(text: string): CanvasTexture {
  const [c, ctx] = mkCanvas(512, 128);
  ctx.clearRect(0, 0, 512, 128);
  ctx.fillStyle = 'rgba(233,228,216,0.85)';
  ctx.textAlign = 'center';
  ctx.font = '700 40px Helvetica, Arial, sans-serif';
  const lines = wrapText(ctx, text, 460);
  lines.forEach((ln, i) => ctx.fillText(ln, 256, 58 + i * 48 - (lines.length - 1) * 22));
  ctx.textAlign = 'left';
  return toTexture(c);
}

export function makeLetterheadBoardTexture(): CanvasTexture {
  const [c, ctx] = mkCanvas(1024, 1024);
  ctx.fillStyle = css(shade(PAL.bone, 0.06));
  ctx.fillRect(0, 0, 1024, 1024);
  ctx.strokeStyle = css(PAL.carbon);
  ctx.lineWidth = 6;
  ctx.strokeRect(24, 24, 976, 976);
  drawTracked(ctx, 'BUREAU OF SATISFACTION', 60, 120, '700 54px Helvetica, Arial, sans-serif', css(PAL.carbon), 8);
  ctx.fillStyle = css(PAL.carbon);
  ctx.fillRect(60, 150, 904, 6);
  drawTracked(ctx, 'DAILY DIRECTIVE', 60, 250, '700 92px Helvetica, Arial, sans-serif', css(PAL.carbon), 10);
  ctx.font = '400 34px ui-monospace, "Courier New", monospace';
  ctx.fillStyle = 'rgba(28,26,23,0.85)';
  ctx.fillText('ISSUED TO EVERY PETITIONER ON EARTH.', 60, 340);
  ctx.fillText('ONE ENTRY PER PETITIONER PER DAY.', 60, 390);
  // ruled lines awaiting today's directive (wired to /api/daily in a later slice)
  ctx.strokeStyle = 'rgba(28,26,23,0.35)';
  ctx.lineWidth = 2;
  for (let y = 480; y <= 780; y += 60) {
    ctx.beginPath();
    ctx.moveTo(60, y);
    ctx.lineTo(964, y);
    ctx.stroke();
  }
  drawTracked(ctx, 'ANSWERED TODAY: PENDING TALLY', 60, 900, '700 30px Helvetica, Arial, sans-serif', css(PAL.carbon), 4);
  ctx.fillStyle = css(PAL.stampRed);
  ctx.fillRect(764, 850, 200, 70);
  ctx.fillStyle = css(shade(PAL.bone, 0.1));
  ctx.font = '700 30px Helvetica, Arial, sans-serif';
  ctx.fillText('DAILY', 790, 895);
  return toTexture(c);
}

export function makePaperFormTexture(): CanvasTexture {
  const [c, ctx] = mkCanvas(512, 704);
  ctx.fillStyle = css(PAL.paper);
  ctx.fillRect(0, 0, 512, 704);
  // tractor-feed holes
  ctx.fillStyle = css(shade(PAL.paper, -0.35));
  for (let y = 18; y < 704; y += 36) {
    ctx.beginPath();
    ctx.arc(16, y, 7, 0, Math.PI * 2);
    ctx.arc(496, y, 7, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.strokeStyle = 'rgba(28,26,23,0.25)';
  ctx.setLineDash([3, 5]);
  ctx.beginPath();
  ctx.moveTo(34, 0);
  ctx.lineTo(34, 704);
  ctx.moveTo(478, 0);
  ctx.lineTo(478, 704);
  ctx.stroke();
  ctx.setLineDash([]);
  drawDotText(ctx, 'FORM 7A', 56, 34, css(PAL.carbon), { dot: 4, inkMiss: 0.05 });
  drawDotText(ctx, 'OFFERING - STANDARD', 56, 82, 'rgba(28,26,23,0.8)', { dot: 3, inkMiss: 0.08 });
  ctx.fillStyle = 'rgba(28,26,23,0.5)';
  for (let y = 160; y < 620; y += 44) ctx.fillRect(56, y, 400, 2);
  return toTexture(c);
}

export function makeFrostTexture(): CanvasTexture {
  const [c, ctx] = mkCanvas(256, 256);
  ctx.fillStyle = 'rgba(244,241,230,0.92)';
  ctx.fillRect(0, 0, 256, 256);
  const rng = streamFrom('frost');
  for (let i = 0; i < 2400; i++) {
    ctx.fillStyle = `rgba(46,76,126,${0.02 + rng() * 0.05})`; // clemency edge tint
    ctx.fillRect(rng() * 256, rng() * 256, 2, 2);
  }
  for (let i = 0; i < 1600; i++) {
    ctx.fillStyle = `rgba(255,255,255,${0.04 + rng() * 0.08})`;
    ctx.fillRect(rng() * 256, rng() * 256, 3, 1);
  }
  return toTexture(c);
}

/** 3-state iris atlas for the eye-lamps: DILATED / NOMINAL / NARROW. */
export function makeIrisAtlas(): CanvasTexture {
  const [c, ctx] = mkCanvas(384, 128);
  const radii = [34, 22, 10];
  for (let i = 0; i < 3; i++) {
    const cx = 64 + i * 128;
    const g = ctx.createRadialGradient(cx, 64, 4, cx, 64, 56);
    g.addColorStop(0, css(shade(PAL.gas, 0.3)));
    g.addColorStop(0.75, css(PAL.gas));
    g.addColorStop(1, css(shade(PAL.gas, -0.3)));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, 64, 56, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = css(PAL.carbon);
    ctx.beginPath();
    ctx.arc(cx, 64, radii[i], 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(233,228,216,0.5)';
    ctx.beginPath();
    ctx.arc(cx - radii[i] * 0.35, 64 - radii[i] * 0.35, radii[i] * 0.22, 0, Math.PI * 2);
    ctx.fill();
  }
  return toTexture(c);
}

/** Overhead designation + Standing label (code-drawn sprite). */
export function makeLabelTexture(designation: string, standing: string): CanvasTexture {
  const [c, ctx] = mkCanvas(256, 96);
  ctx.clearRect(0, 0, 256, 96);
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(28,26,23,0.78)';
  ctx.font = '700 22px ui-monospace, "Courier New", monospace';
  ctx.fillText(designation, 128, 34);
  ctx.font = '400 18px ui-monospace, "Courier New", monospace';
  ctx.fillStyle = 'rgba(28,26,23,0.6)';
  ctx.fillText(standing, 128, 64);
  ctx.textAlign = 'left';
  return toTexture(c);
}
